import { motion } from "framer-motion";
import { memo, useMemo } from "react";
import IconButton from "../components/IconButton.tsx";
import Modal from "../feedback/Modal.tsx";
import { getFileType } from "../utils/validators.ts";
import "./data-display.css";

type ModalFile =
	| string
	| {
			url?: string;
			nome?: string;
			tipo?: string;
	  };

interface ModalImageProps {
	image?: ModalFile;
	onClose: () => void;
	isOpen: boolean;
}

function ModalImage({ image, onClose, isOpen }: ModalImageProps) {
	const url = useMemo(() => {
		if (!image) return "";
		if (typeof image === "string") return image;
		return image.url ?? "";
	}, [image]);

	const mime = useMemo(() => {
		if (!image || typeof image === "string") return "";
		return image.tipo ?? "";
	}, [image]);

	const nome = useMemo(() => {
		if (!image || typeof image === "string") return "";
		return image.nome ?? "";
	}, [image]);

	const kind = useMemo(() => {
		if (mime) {
			if (mime.startsWith("image/")) return "image";
			if (mime === "application/pdf") return "pdf";
		}

		if (nome) return getFileType(nome);

		if (url) return getFileType(url);

		return "";
	}, [mime, nome, url]);

	const isImage = kind === "image" || kind === "blob";
	const isPdf = kind === "pdf";

	if (!url) return null;

	return (
		<Modal isOpen={isOpen} onClose={onClose}>
			<motion.div
				className="modal-image-content"
				initial={{ scale: 0.8 }}
				animate={{ scale: 1 }}
				exit={{ scale: 0.8 }}
				onClick={(e) => e.stopPropagation()}
			>
				{isImage && (
					<motion.img
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.3 }}
						src={url}
						alt="Modal Preview"
					/>
				)}

				{isPdf && (
					<embed
						src={url}
						type={mime || "application/pdf"}
						style={{ minWidth: "800px", minHeight: "800px" }}
					/>
				)}

				{!isImage && !isPdf && (
					<div style={{ padding: 16 }}>
						<p style={{ marginBottom: 12 }}>Não foi possível pré-visualizar este arquivo.</p>
						<a href={url} target="_blank" rel="noreferrer">
							Abrir arquivo
						</a>
					</div>
				)}

				<div style={{ position: "absolute", top: 10, right: 10 }}>
					<IconButton
						icon="X"
						size="lg"
						onClick={onClose}
						backgroundColor="#00000080"
						hoverColor="#ffffff80"
					/>
				</div>
			</motion.div>
		</Modal>
	);
}

export default memo(ModalImage);
